import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './MDSC_SignInThirdParty.html?raw';

import _WidgetBaseSP from 'more/_WidgetBaseSP';
import { ThirdPartyAppName, thirdPartySignInConfig, eventHooks, ThirdPartyAppServerConfig, urlRedirectConfig } from "./MDSC_ThirdParty_types";
import lang from 'dojo/_base/lang';
import GenericDialog from '../../../moreCore/UI/GenericDialog/GenericDialog';
import { MDSC_IFrameEventsHandler } from "./IFrameEvents/MDSC_TP_Events";

class MDSC_SignInThirdParty extends compose(_WidgetBaseSP) {
    templateString: any = template;

    private dlgContentContainer:HTMLDivElement;
    private appNameSpan:HTMLSpanElement;

    private iframe_event_handler: MDSC_IFrameEventsHandler|null = null;
    postCreate(): void {
        super.postCreate.apply(this, arguments as any);
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }

    private async getServerAppList(): Promise<Array<ThirdPartyAppServerConfig>> {
        const cacheKey = "mdsc_thirdpartyapplistcache";
        let appList: Array<ThirdPartyAppServerConfig> = [];

        let fetchListFromServer = false;

        try {
            const cacheRaw = localStorage.getItem(cacheKey);
            if (cacheRaw) {
                const cache: { exp: number; data: Array<ThirdPartyAppServerConfig> } = JSON.parse(cacheRaw);
                const now = Date.now();

                if (cache.exp > now) {
                    appList = cache.data;
                } else {
                    fetchListFromServer = true;
                }
            } else {
                fetchListFromServer = true;
            }
        } catch (e) {
            console.error("cannot access local storage?!", e);
            fetchListFromServer = true;
        }

        if (fetchListFromServer){
            let prom:Promise<any> = new Promise((resolve, reject) => {
                this.RequestManager.callfunction(
                    "Webtool_API_MDSConnect_GetListOfApps",
                    {},
                    lang.hitch(this, (result:any) => {
                        resolve(result);
                    }),
                    lang.hitch(this, (err) => {
                        console.error(err.Message);
                        this.showToast(err.Message, "fas fa-exclamation-circle");
                        reject(err);
                    })
                );
            });

            let result:Array<ThirdPartyAppServerConfig>;
            try{
                result = await prom;
            }catch{
                return null;
            }
            if (!result){
                throw "invalid data recieved";
            }
            if (typeof result == 'string'){
                result = JSON.parse(result);
            }

            let exp: number = Date.now() + 2 * (24 * 60 * 60 * 1000 ); // now + 2 tage, weil aendert sich nicht wirklich
            let cacheStr = JSON.stringify({exp:exp, data:result});
            try{
                localStorage.setItem(cacheKey, cacheStr);
            }catch(e){
                console.error("cannot cache app list", e);
            }

            appList = result;
        }

        return appList;
    }


    /**
     * @async
     * Initiates the sign-in process for a third-party application.
     *
     * @param {ThirdPartyAppName} appName - Der Name der App wie sie auch im MDSC Service hinterlegt worden ist.
     * @param {thirdPartySignInConfig} appConfig - Konfigurationseinstellungen für die Anmeldung über Drittanbieter, einschließlich Zielinteraktion (iFrame, Popup, Weiterleitung) und zugehörige Einstellungen.
     * @param {eventHooks} [eventHooks] - Optionale Ereignishooks, die in verschiedenen Phasen des Anmeldevorgangs ausgeführt werden können, z. B. vor dem Start, vor der Weiterleitung und nach Abschluss. Enthält Fehlerbehandlung.
     * @param {boolean} [showAllowPermissionDlg=false] - Legt fest, ob dem Benutzer vor dem Start des Anmeldevorgangs ein Berechtigungsdialogfeld angezeigt werden soll.
     *
     * @returns {Promise<void>} - Ein Promise der aufgelöst wird, wenn der Vorgang abgeschlossen worden ist.
     *
     * @remarks
     * Diese Methode Handled alles zur Anmeldung oder Session Transfer ueber MDSC.
     */
    public async SignInUser(appName: ThirdPartyAppName, appConfig: thirdPartySignInConfig, eventHooks?: eventHooks, showAllowPermissionDlg:boolean=false){
        // Call beforeStart hook if set
        if (eventHooks?.beforeStart) {
            try {
                const shouldContinue = await eventHooks.beforeStart(appName);
                if (shouldContinue === false) {
                    return;
                }
            } catch (error) {
                if (eventHooks?.onError) {
                    await eventHooks.onError(appName, "Error in beforeStart hook: " + error.toString());
                }
                return;
            }
        }

        let appList: Array<ThirdPartyAppServerConfig>;
        try{
            appList = await this.getServerAppList();
            if (appList == null){
                if (eventHooks?.onError) {
                    await eventHooks.onError(appName, "Failed to get app list from server");
                }
                return;
            }
            if (appList.length < 1){
                if (eventHooks?.onError) {
                    await eventHooks.onError(appName, "No apps found on server");
                }
                return;
            }
        }catch(error){
            if (eventHooks?.onError) {
                await eventHooks.onError(appName, "Error fetching app list: " + error.toString());
            }
            return;
        }

        let appDisplayName;
        let isAvailable = false;
        for (let app of appList){
            if (app.internalName.toLowerCase() == appName.toLowerCase()){
                isAvailable = true;
                appName = app.internalName as any;
                appDisplayName = app.displayName ?? app.internalName;
            }
        }

        if (isAvailable == false){
            this.showToast(this.translate("MDSConnect_3rdParty__ServerSideNotAvailable"), "fas fa-exclamation");
            return;
        }

        if (showAllowPermissionDlg == true){
            let gotUserPermission = true;
            console.log("going to ask user permission");
            gotUserPermission = await this.showPermissionDlg(appDisplayName);
            console.log("gotUserPermission", gotUserPermission);
            if (gotUserPermission != true){
                try {
                    if (eventHooks?.onPermissionDeniedByUser) {
                        await eventHooks.onPermissionDeniedByUser(appName);
                    }
                    if (eventHooks?.onOperationFinished) {
                        await eventHooks.onOperationFinished(appName);
                    }
                } catch (error) {
                    if (eventHooks?.onError) {
                        await eventHooks.onError(appName, "Error in permission denied hooks: " + error.toString());
                    }
                }
                return;
            }
        }

        let prom:Promise<any> = new Promise((resolve, reject) => {
            this.RequestManager.callfunction(
                "Webtool_API_MDSConnect_OpenThirdPartySession",
                {
                    lang: window["currentLanguage"] ?? "ENGLISH",
                    targetApp:appName
                },
                lang.hitch(this, (result:any) => {
                    resolve(result);
                }),
                lang.hitch(this, (err) => {
                    console.error(err.Message);
                    this.showToast(err.Message, "fas fa-exclamation-triangle");
                    reject(err);
                })
            );
        });

        let result: { redirectURL: string, transferToken: string }; //redirectURL?stt=transferToken
        try{
            result = await prom;
        }catch(e){
            console.error("cannot open session transfer", e);
            return;
        }

        //NOTE - hier koennte man nach bedarf statisch je nach appName noch statisch urlRedirectConfig vordefinieren
        // Idealerweise können wir aber davon ausgehen, dass die URL vom server stimmt. (außer evtl. behind reverse proxy oder andere lustige edge-cases)
        let redirectUrlConfig: urlRedirectConfig = {
            baseUrl: result.redirectURL,
            stt: result.transferToken,
            urlBuildTemplate:"{baseUrl}?stt={token}&mdsc_session_nonce={nonce}" //stt - session transfer token
        };

        // Call beforeJumpOff hook if set
        if (eventHooks?.beforeJumpOff) {
            try {
                const shouldContinue = await eventHooks.beforeJumpOff(appName, redirectUrlConfig );
                if (shouldContinue === false) {
                    return;
                }
            } catch (error) {
                if (eventHooks?.onError) {
                    await eventHooks.onError(appName, "Error in beforeJumpOff hook: " + error.toString());
                }
                return;
            }
        }

        let iframe_nonce = this.generateRandomString(25);

        let urlBuilding = redirectUrlConfig.urlBuildTemplate;
        urlBuilding = urlBuilding.replace("{baseUrl}",redirectUrlConfig.baseUrl);
        urlBuilding = urlBuilding.replace("{token}", encodeURIComponent(redirectUrlConfig.stt));
        urlBuilding = urlBuilding.replace("{nonce}", iframe_nonce);
        let fullRedirectUrl: string = urlBuilding;

        switch (appConfig.targetInteraction){
            case 'iFrame':
                if (appConfig.iframeConfig?.targetContainer) {
                    // Opt-out path: when the loaded iframe owns its own postMessage protocol
                    // (e.g. evisio2 SDK), skip the nonce-validated listener to avoid
                    // "malformed message data" warnings on every non-MDSC postMessage.
                    if (appConfig.iframeConfig.skipIframeEvents !== true){
                        this.iframe_event_handler = new MDSC_IFrameEventsHandler(redirectUrlConfig.baseUrl, iframe_nonce);
                    }
                    const iframe = document.createElement('iframe');
                    iframe.src = fullRedirectUrl;

                    // Apply all optional iframe configs
                    if (appConfig.iframeConfig.title) {
                        iframe.title = appConfig.iframeConfig.title;
                    }
                    if (appConfig.iframeConfig.name) {
                        iframe.name = appConfig.iframeConfig.name;
                    }
                    if (appConfig.iframeConfig.srcdoc) {
                        iframe.srcdoc = appConfig.iframeConfig.srcdoc;
                    }
                    if (appConfig.iframeConfig.referrerPolicy) {
                        iframe.referrerPolicy = appConfig.iframeConfig.referrerPolicy;
                    }
                    if (appConfig.iframeConfig.sandbox) {
                        iframe.sandbox.value = appConfig.iframeConfig.sandbox;
                    }
                    if (appConfig.iframeConfig.allow) {
                        iframe.allow = appConfig.iframeConfig.allow;
                    }
                    if (appConfig.iframeConfig.allowFullscreen) {
                        iframe.allowFullscreen = appConfig.iframeConfig.allowFullscreen;
                    }

                    // Apply size if specified
                    if (appConfig.iframeConfig.iframeSize) {
                        iframe.style.width = appConfig.iframeConfig.iframeSize.width.toString();
                        iframe.style.height = appConfig.iframeConfig.iframeSize.height.toString();
                    }

                    appConfig.iframeConfig.targetContainer.appendChild(iframe);

                    try {
                        if (eventHooks?.onIframeReady) {
                            await eventHooks.onIframeReady(appName, iframe);
                        }
                        if (eventHooks?.onOperationFinished) {
                            await eventHooks.onOperationFinished(appName);
                        }
                    } catch (error) {
                        if (eventHooks?.onError) {
                            await eventHooks.onError(appName, "Error in iframe hooks: " + error.toString());
                        }
                        return;
                    }
                }else{
                    if (eventHooks?.onError) {
                        await eventHooks.onError(appName, "Missing iframe configuration");
                    }
                    console.warn("dev: implementation configuration error");
                    return;
                }
                break;
            case 'popup':
                if (appConfig.popupConfig) {
                    let popupFeatures = 'popup=true';
                    if (appConfig.popupConfig.popupSize) {
                        popupFeatures += `,width=${appConfig.popupConfig.popupSize.width},height=${appConfig.popupConfig.popupSize.height}`;
                    }
                    if (appConfig.popupConfig.popupPosition) {
                        if (typeof appConfig.popupConfig.popupPosition === 'string') {
                            popupFeatures += `,${appConfig.popupConfig.popupPosition}`;
                        } else {
                            popupFeatures += `,left=${appConfig.popupConfig.popupPosition.left},top=${appConfig.popupConfig.popupPosition.top}`;
                        }
                    }

                    const popup = window.open(fullRedirectUrl, appConfig.popupConfig.popupTitle, popupFeatures);
                    if (popup && appConfig.popupConfig.focusOnOpen) {
                        popup.focus();
                    }

                    try {
                        if (eventHooks?.onPopupReady) {
                            await eventHooks.onPopupReady(appName);
                        }
                        if (eventHooks?.onOperationFinished) {
                            await eventHooks.onOperationFinished(appName);
                        }
                    } catch (error) {
                        if (eventHooks?.onError) {
                            await eventHooks.onError(appName, "Error in popup hooks: " + error.toString());
                        }
                        return;
                    }
                }else{
                    if (eventHooks?.onError) {
                        await eventHooks.onError(appName, "Missing popup configuration");
                    }
                    console.warn("dev: implementation configuration error");
                    return;
                }
                break;
            case 'redirectSameTab':
                if (appConfig.newTabOrRedirectConfig){
                    const config = appConfig.newTabOrRedirectConfig || {};
                    try {
                        if (eventHooks?.onOperationFinished) {
                            await eventHooks.onOperationFinished(appName);
                        }
                        if (config.replaceHistory) {
                            window.location.replace(fullRedirectUrl);
                        } else {
                            window.location.href = fullRedirectUrl;
                        }
                    } catch (error) {
                        if (eventHooks?.onError) {
                            await eventHooks.onError(appName, "Error in redirect hooks: " + error.toString());
                        }
                    }
                }else{
                    if (eventHooks?.onError) {
                        await eventHooks.onError(appName, "Missing redirect configuration");
                    }
                    console.warn("dev: implementation configuration error");
                    return;
                }
                break;

            default: //default oder 'newTab'
                const newTabConfig = appConfig.newTabOrRedirectConfig || {};
                const target = newTabConfig.linkTarget || '_blank';
                const features = newTabConfig.openInBackground ? 'noopener' : 'noopener,noreferrer';
                try {
                    window.open(fullRedirectUrl, target, features);
                    if (eventHooks?.onOperationFinished) {
                        await eventHooks.onOperationFinished(appName);
                    }
                } catch (error) {
                    if (eventHooks?.onError) {
                        await eventHooks.onError(appName, "Error opening new tab: " + error.toString());
                    }
                }
                break;
        }
    }

    private askUserForPermissionDlg: MdsLax<InstanceType<typeof GenericDialog>> = null;
    private async showPermissionDlg(appDisplayName:string):Promise<boolean>{
        this.appNameSpan.innerHTML = appDisplayName;
        if (this.askUserForPermissionDlg == null){
            this.askUserForPermissionDlg = new GenericDialog();
        }
        this.askUserForPermissionDlg.setSize('smaller');
        return new Promise((resolve)=>{
            this.askUserForPermissionDlg.addButton(
                "abortOperation",
                this.translate("GenericDialog__Btn_Cancel"),
                ()=>{
                    resolve(false);
                },
                null,
                "tertiaryBG",
                "fas fa-times",
                true,
                'last',
                true
            );
            this.askUserForPermissionDlg.addButton(
                "shareProfile",
                this.translate("MDSConnect_3rdParty_PermissionDlg_Allow"),
                ()=>{
                    resolve(true);
                },
                null,
                "primaryBG",
                "fas fa-check",
                true,
                'first',
                true
            );
            this.askUserForPermissionDlg.removeButton('Ok');
            this.askUserForPermissionDlg.show(
                this.dlgContentContainer,
                this.translate("MDSConnect_name"),
                null,
                "fas fa-link w3-xxxlarge w3-padding-large",
                null
            );
        });
    }

    private generateRandomString(length: number): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';

        try {
            // Try to use Web Crypto API
            const randomValues = new Uint8Array(length);
            crypto.getRandomValues(randomValues);
            for (let i = 0; i < length; i++) {
                result += chars[randomValues[i] % chars.length];
            }
        } catch (e) {
            // Fallback to Math.random
            console.warn("Web Crypto API unavailable, falling back to Math.random", e);
            for (let i = 0; i < length; i++) {
                result += chars[Math.floor(Math.random() * chars.length)];
            }
        }

        return result;
    }

    /**
     * Unterbinded das Handlen von iFrameEvents
     */
    public deregisterIframeEventHandling(){
        if (this.iframe_event_handler){
            if (this.iframe_event_handler != null){
                this.iframe_event_handler.destroy();
                this.iframe_event_handler = null;
                delete this.iframe_event_handler
            }
        }
    }

    //ADD HERE
}

export default mdsWidget(MDSC_SignInThirdParty);
